import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  BanknotesIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CubeIcon,
  GlobeAltIcon,
  BeakerIcon,
  CpuChipIcon,
  TruckIcon,
  ShoppingBagIcon,
  SunIcon,
  HeartIcon,
  WrenchScrewdriverIcon,
  RocketLaunchIcon,
  CircleStackIcon,
  DevicePhoneMobileIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

function Portfolio() {
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const industries = [
    {
      id: 'tech',
      name: 'Technology',
      icon: CpuChipIcon,
      percentage: 25,
      description: 'Leading tech companies and emerging startups in AI, cloud computing, and cybersecurity.',
      companies: ['Artificial Intelligence', 'Cloud Services', 'Cybersecurity', '5G Networks'],
      color: 'bg-blue-500'
    },
    {
      id: 'finance',
      name: 'Financial Services',
      icon: BanknotesIcon,
      percentage: 20,
      description: 'Digital payment solutions, fintech innovations, and blockchain technology.',
      companies: ['Digital Banking', 'Payment Processing', 'Blockchain', 'Insurance Tech'],
      color: 'bg-green-500'
    },
    {
      id: 'realestate',
      name: 'Real Estate',
      icon: BuildingOfficeIcon,
      percentage: 15,
      description: 'Premium commercial properties, residential developments, and REITs.',
      companies: ['Commercial Properties', 'Residential Developments', 'Data Centers', 'Industrial Facilities'],
      color: 'bg-yellow-500'
    },
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: HeartIcon,
      percentage: 10,
      description: 'Biotechnology, medical devices, and healthcare technology solutions.',
      companies: ['Biotech Research', 'Medical Devices', 'Digital Health', 'Pharmaceuticals'],
      color: 'bg-red-500'
    },
    {
      id: 'energy',
      name: 'Clean Energy',
      icon: SunIcon,
      percentage: 10,
      description: 'Renewable energy projects, sustainable technologies, and clean energy infrastructure.',
      companies: ['Solar Power', 'Wind Energy', 'Energy Storage', 'Smart Grid'],
      color: 'bg-orange-500'
    },
    {
      id: 'manufacturing',
      name: 'Advanced Manufacturing',
      icon: WrenchScrewdriverIcon,
      percentage: 5,
      description: '3D printing, robotics, and smart manufacturing solutions.',
      companies: ['3D Printing', 'Robotics', 'IoT Manufacturing', 'Smart Factories'],
      color: 'bg-purple-500'
    },
    {
      id: 'ecommerce',
      name: 'E-Commerce',
      icon: ShoppingBagIcon,
      percentage: 5,
      description: 'Digital retail platforms, logistics, and e-commerce technology.',
      companies: ['Online Retail', 'Digital Marketplaces', 'Logistics Tech', 'Payment Solutions'],
      color: 'bg-pink-500'
    },
    {
      id: 'logistics',
      name: 'Transportation & Logistics',
      icon: TruckIcon,
      percentage: 5,
      description: 'Electric vehicles, autonomous transportation, and smart logistics.',
      companies: ['Electric Vehicles', 'Autonomous Tech', 'Smart Logistics', 'Fleet Management'],
      color: 'bg-indigo-500'
    },
    {
      id: 'crypto',
      name: 'Cryptocurrency',
      icon: CircleStackIcon,
      percentage: 5,
      description: 'Digital assets, DeFi protocols, and blockchain infrastructure.',
      companies: ['Bitcoin', 'Ethereum', 'DeFi Protocols', 'NFT Platforms'],
      color: 'bg-violet-500'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <motion.section 
        className="pt-24 pb-12 bg-gradient-to-r from-primary-900 to-primary-800"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              className="text-4xl font-bold text-white mb-4"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Our Investment Portfolio
            </motion.h1>
            <motion.p 
              className="text-xl text-primary-100 max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Diversified investments across high-growth industries, delivering consistent returns through strategic asset allocation.
            </motion.p>
          </div>
        </div>
      </motion.section>

      {/* Portfolio Overview */}
      <motion.section 
        className="py-16"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => (
              <motion.div
                key={industry.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                variants={itemVariants}
                whileHover={{ y: -5 }}
                onClick={() => setSelectedIndustry(industry)}
              >
                <div className={`h-2 ${industry.color}`} />
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <industry.icon className="h-8 w-8 text-primary-600 mr-3" />
                    <h3 className="text-xl font-semibold text-gray-900">{industry.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{industry.description}</p>
                  <div className="flex justify-between items-center">
                    <div className="text-2xl font-bold text-primary-600">{industry.percentage}%</div>
                    <div className="text-sm text-gray-500">Portfolio Allocation</div>
                    </div>
                  <div className="mt-4">
                    <div className="text-sm text-gray-600 mb-2">Key Investments:</div>
                    <div className="flex flex-wrap gap-2">
                      {industry.companies.map((company, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                        >
                          {company}
                      </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Portfolio Stats */}
      <motion.section 
        className="py-16 bg-gray-900 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div 
              className="text-center"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-primary-400">$500M+</div>
              <div className="mt-2 text-gray-400">Assets Under Management</div>
            </motion.div>
            <motion.div 
              className="text-center"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-primary-400">9</div>
              <div className="mt-2 text-gray-400">Industry Sectors</div>
            </motion.div>
            <motion.div 
              className="text-center"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-primary-400">150%</div>
              <div className="mt-2 text-gray-400">Monthly Returns</div>
            </motion.div>
            <motion.div 
              className="text-center"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-primary-400">24/7</div>
              <div className="mt-2 text-gray-400">Portfolio Monitoring</div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Investment Process */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Investment Process</h2>
            <p className="mt-4 text-lg text-gray-600">Strategic approach to portfolio management and growth</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: ChartBarIcon,
                title: "Market Analysis",
                description: "Continuous monitoring of market trends and opportunities"
              },
              {
                icon: GlobeAltIcon,
                title: "Diversification",
                description: "Strategic allocation across multiple sectors and regions"
              },
              {
                icon: BeakerIcon,
                title: "Risk Management",
                description: "Advanced risk assessment and mitigation strategies"
              },
              {
                icon: RocketLaunchIcon,
                title: "Growth Optimization",
                description: "Maximizing returns through strategic positioning"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * index }}
              >
                <step.icon className="h-12 w-12 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}

export default Portfolio; 